import { eventHasPassOnTicketGst, ticketCustomerPriceCents, ticketPriceGstSuffix } from './booking-fee'

/**
 * Price chip wording shared by Discover and the public facilitator profile's
 * event cards (#1019, Ed 2026-09-19: "the event cards get their price the way
 * Discover already does").
 *
 * Both surfaces read the same plain `ticket_types` include, so a member sees
 * the same "From $45" on the profile as on Discover. Only the cheapest *paid*
 * tier sets the figure; "From" appears when a free or donation tier sits
 * beside it. Whole dollars drop the cents.
 */
export type PriceTicketType = {
  ticket_kind: 'PAID' | 'FREE' | 'DONATION' | string
  price_cents: number
  // #1440 (deposit tickets, phase 1) — the full price, when Path only
  // charges a deposit (price_cents).
  advertised_price_cents?: number | null
  // #1497 — GST-inclusive customer price; equals price_cents except for a
  // GST-registered PASS_ON facilitator. Same field #1480 added to the
  // checkout-flow ticket-types endpoint; the list endpoints (Discover,
  // `available`, a facilitator's public event feed) now compute it too.
  customer_price_cents?: number | null
}

function dollars(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`
}

export function priceChipLabel(
  ticketTypes: PriceTicketType[] | null | undefined,
): string | null {
  if (!ticketTypes?.length) return null
  const paid = ticketTypes.filter(
    (t): t is PriceTicketType & { ticket_kind: 'PAID' } => t.ticket_kind === 'PAID' && t.price_cents > 0,
  )
  if (paid.length > 0) {
    // #1497 — the chip must show the same GST-inclusive figure checkout
    // charges for a GST-registered PASS_ON facilitator. GST is a uniform
    // per-facilitator markup, so it never changes which tier is cheapest —
    // only the figure displayed for it.
    const cents = Math.min(...paid.map((t) => ticketCustomerPriceCents(t)))
    const suffix = ticketPriceGstSuffix(eventHasPassOnTicketGst(paid))
    const base = `${dollars(cents)}${suffix}`
    return ticketTypes.length > paid.length ? `From ${base}` : base
  }
  if (ticketTypes.some((t) => t.ticket_kind === 'DONATION')) return 'Donation'
  return 'Free'
}

/**
 * #1440 FR4 — event cards and Discover listings show the full price with a
 * small "deposit $X" note when the cheapest paid tier is a deposit ticket.
 * The price *filter* (separate UI) keeps sorting/filtering on `price_cents`
 * — this only changes what the card's chip displays.
 */
export function priceChipWithDeposit(
  ticketTypes: PriceTicketType[] | null | undefined,
): { label: string; depositNote: string | null } | null {
  if (!ticketTypes?.length) return null
  const paid = ticketTypes.filter(
    (t): t is PriceTicketType & { ticket_kind: 'PAID' } => t.ticket_kind === 'PAID' && t.price_cents > 0,
  )
  if (paid.length > 0) {
    const cheapest = paid.reduce((min, t) => (t.price_cents < min.price_cents ? t : min))
    if (cheapest.advertised_price_cents != null && cheapest.advertised_price_cents > cheapest.price_cents) {
      // #1497 — the full advertised price is never GST-processed
      // server-side (billing-payments.md) — it is a static reference figure,
      // so no "(incl. GST)" suffix belongs on it. Only the deposit charged
      // today is GST-inclusive, same precedent as the checkout pages'
      // depositBalanceLabel (#1480 round 2).
      const label = dollars(cheapest.advertised_price_cents)
      return {
        label: ticketTypes.length > paid.length ? `From ${label}` : label,
        depositNote: `deposit ${dollars(ticketCustomerPriceCents(cheapest))}`,
      }
    }
  }
  const label = priceChipLabel(ticketTypes)
  return label ? { label, depositNote: null } : null
}
